import React from 'react';

export const parseTimeParts = (timeStr) => {
   if (!timeStr || typeof timeStr !== 'string') {
      return { hour: '', minute: '', period: 'AM' };
   }
   const trimmed = timeStr.trim();
   if (!trimmed) {
      return { hour: '', minute: '', period: 'AM' };
   }

   // 12-hour format: "09:30 AM", "9:30 AM", "2:15 PM"
   const ampmMatch = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
   if (ampmMatch) {
      let h = parseInt(ampmMatch[1], 10);
      if (isNaN(h)) return { hour: '', minute: '', period: 'AM' };
      if (h === 0) h = 12;
      if (h > 12) h = h % 12 || 12;
      const hStr = h < 10 ? `0${h}` : `${h}`;
      return {
         hour: hStr,
         minute: ampmMatch[2],
         period: ampmMatch[3].toUpperCase()
      };
   }

   // 24-hour format: "14:30", "09:00"
   const h24Match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
   if (h24Match) {
      let h = parseInt(h24Match[1], 10);
      if (isNaN(h)) return { hour: '', minute: '', period: 'AM' };
      const period = h >= 12 ? 'PM' : 'AM';
      h = h % 12;
      if (h === 0) h = 12;
      const hStr = h < 10 ? `0${h}` : `${h}`;
      return {
         hour: hStr,
         minute: h24Match[2],
         period
      };
   }

   return { hour: '', minute: '', period: 'AM' };
};

export const formatTime12 = (timeStr, fallback = 'TBD') => {
   if (!timeStr) return fallback;
   const pt = parseTimeParts(timeStr);
   if (pt.hour && pt.minute) {
      return `${pt.hour}:${pt.minute} ${pt.period}`;
   }
   return timeStr || fallback;
};

const TimePicker12 = ({ value, onChange, className = '', disabled = false }) => {
   const timeParts = parseTimeParts(value);

   const updateTime = (newHour, newMinute, newPeriod) => {
      const h = newHour || timeParts.hour || '10';
      const m = newMinute !== undefined && newMinute !== '' ? newMinute : (timeParts.minute || '00');
      const p = newPeriod || timeParts.period || 'AM';
      const formatted = `${h}:${m} ${p}`;
      onChange(formatted);
   };

   return (
      <div className={`grid grid-cols-3 gap-1.5 p-1 bg-white border-2 border-slate-100 rounded-xl focus-within:border-violet-500 transition-all ${className} ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
         {/* Hour Select */}
         <select
            disabled={disabled}
            className="w-full p-2.5 rounded-lg font-bold text-xs bg-slate-50 text-slate-800 outline-none cursor-pointer hover:bg-slate-100 transition-colors"
            value={timeParts.hour}
            onChange={(e) => updateTime(e.target.value, timeParts.minute || '00', timeParts.period)}
         >
            <option value="">Hour</option>
            {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map((h) => (
               <option key={h} value={h}>{h}</option>
            ))}
         </select>

         {/* Minute Select */}
         <select
            disabled={disabled}
            className="w-full p-2.5 rounded-lg font-bold text-xs bg-slate-50 text-slate-800 outline-none cursor-pointer hover:bg-slate-100 transition-colors"
            value={timeParts.minute}
            onChange={(e) => updateTime(timeParts.hour || '10', e.target.value, timeParts.period)}
         >
            <option value="">Min</option>
            {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map((m) => (
               <option key={m} value={m}>{m}</option>
            ))}
         </select>

         {/* AM/PM Toggle */}
         <div className="flex rounded-lg overflow-hidden bg-slate-100 p-0.5 border border-slate-200/60">
            <button
               type="button"
               disabled={disabled}
               onClick={() => updateTime(timeParts.hour || '10', timeParts.minute || '00', 'AM')}
               className={`flex-1 text-[10px] font-black rounded-md transition-all ${timeParts.period === 'AM'
                     ? 'bg-violet-600 text-white shadow-sm'
                     : 'text-slate-500 hover:text-slate-800'
                  }`}
            >
               AM
            </button>
            <button
               type="button"
               disabled={disabled}
               onClick={() => updateTime(timeParts.hour || '10', timeParts.minute || '00', 'PM')}
               className={`flex-1 text-[10px] font-black rounded-md transition-all ${timeParts.period === 'PM'
                     ? 'bg-violet-600 text-white shadow-sm'
                     : 'text-slate-500 hover:text-slate-800'
                  }`}
            >
               PM
            </button>
         </div>
      </div>
   );
};

export default TimePicker12;
